import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Checkbox from '@pelckmans/elementis/components/checkbox';
import Dialog from '@pelckmans/elementis/components/dialog';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import Pill from '@pelckmans/elementis/components/pill';
import List from '@pelckmans/elementis/components/list';
import Tasks from '@pelckmans/elementis/icons/tasks';
import User from '@pelckmans/elementis/icons/user';
import SuccessIcon from '@pelckmans/elementis/icons/success';
import { useContext, useEffect, useState } from 'react';
import ModalContext from '../../../../contexts/ModalContext';
import { formatDate } from '../../../../utils/formatDate';
import { useTranslation } from 'react-i18next';
import getAccessToken from '../../../../api/BearerToken';
import api from '../../../../api';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import { getExerciseAssignmentSession } from '../../../../api/material';
import { MaterialIcon } from '../../../../pages/material/components/MaterialIcon';
import LoadingState from '../../../LoadingState';

const tokenAccessor = { tokenGetter: getAccessToken };

type FormError = 'PRECHECK' | 'SUBMIT' | null;

export type Material = {
  id: string;
  name: string;
  contentType?: {
    name: string;
    icon?: string;
    iconClassName?: string;
  };
  isDone: boolean;
  type: 'medialink' | 'user-material';
  href?: string;
};

export type MaterialAssignment = Material & {
  moduleId: string;
  assignment: {
    id: string;
    to: string;
  };
  owner: {
    firstName: string;
    lastName: string;
  };
};

interface Props {
  id: string;
  material: Omit<MaterialAssignment, 'isDone'>;
  onOpenMaterial: (material: Material) => void;
  onModalClosed: (material: Material) => void;
}

const BASE = 'PAGES.TOC_LEVEL.SECTIONS.STUDENT.ASSIGNMENTS.MATERIAL_ASSIGNMENTS.DIALOG';
const studioGroup = process.env.REACT_APP_P_STUDIO_GROUP;

function MaterialAssignmentModal({ id, material, onOpenMaterial, onModalClosed }: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const handleError = useThrowAsyncError();

  const [opened, setOpened] = useState(false);
  const [checked, setChecked] = useState(false);
  const [formError, setFormError] = useState<FormError>(null);
  const [isDone, setIsDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function fetchExerciseAssignmentSession() {
      setIsLoading(true);
      const { result } = await getExerciseAssignmentSession(material.moduleId, material.assignment.id);
      setIsDone(Boolean(result));
      setIsLoading(false);
    }

    fetchExerciseAssignmentSession().catch(handleError);
  }, [material, handleError]);

  function handleClose() {
    closeModal(id);
  }

  async function handleSubmit() {
    const token = await tokenAccessor.tokenGetter();
    const headers = { Authorization: `Bearer ${token}`, studioGroup };

    try {
      await api.post(
        `/studio/user/modules/${material.moduleId}/assignments/${material.assignment.id}/exercise-session/closed`,
        undefined,
        { headers },
      );
    } catch (e) {
      handleError(e);
    } finally {
      closeModal(id);
      onModalClosed({ ...material, isDone: checked });
    }
  }

  function handleCheckboxChange() {
    if (opened) {
      setChecked(!checked);
      setFormError(null);
    } else {
      setFormError('PRECHECK');
    }
  }

  function handleSubmitClick() {
    if (opened && checked) {
      handleSubmit();
    } else {
      setFormError('SUBMIT');
    }
  }

  function handleOpen() {
    setOpened(true);
    setFormError(null);
  }

  const isLink = Boolean(material.href);

  const renderAlert = () => {
    if (formError === 'PRECHECK') {
      return <Alert prominence="warning">{t(`${BASE}.ALERT.PRECHECK`)}</Alert>;
    }
    if (formError === 'SUBMIT') {
      return <Alert prominence="danger">{t(`${BASE}.ALERT.SUBMIT`)}</Alert>;
    }
    return null;
  };

  return (
    <Dialog id={id} modal onClose={handleClose} size="md" aria-label={t(`${BASE}.A11Y_LABEL`)}>
      <Dialog.Header title={material.name} onClose={handleClose} aria-label={t(`${BASE}.CLOSE.A11Y_LABEL`)}>
        <PillGroup role={undefined} aria-label={undefined}>
          <Pill icon={<Tasks />}>{t('PAGES.TOC_LEVEL.MATERIAL_PILLS.ASSIGNMENT')}</Pill>
          <Pill>{t(`${BASE}.TO`, { to: formatDate(material.assignment.to) })}</Pill>
          <Pill icon={<User />}>{`${material.owner.firstName} ${material.owner.lastName}`}</Pill>
        </PillGroup>
      </Dialog.Header>
      <Dialog.Body
        alertGroupProps={
          formError && !isLoading
            ? {
                'aria-label': undefined,
                role: undefined,
                children: renderAlert()!,
              }
            : undefined
        }
      >
        {isLoading ? (
          <LoadingState size={2} variant="list" aria-label={t(`${BASE}.LIST.LOADING.A11Y_LABEL`)} />
        ) : (
          <List>
            <List.Header title={t(`${BASE}.LIST.TITLE`)} titleTag="h4" />
            <List.Item
              icon={<MaterialIcon materialType={material.type} contentType={material.contentType} />}
              key={material.id}
              title={material.name}
              actionArea={
                <>
                  {isLink ? (
                    <Button
                      key="open-link"
                      prominence="neutral"
                      href={material.href!}
                      as="link"
                      size="sm"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleOpen}
                    >
                      {t(`${BASE}.LIST.OPEN`)}
                    </Button>
                  ) : (
                    <Button
                      key="open"
                      size="sm"
                      prominence="neutral"
                      onClick={() => {
                        handleOpen();
                        onOpenMaterial({ ...material, isDone });
                      }}
                    >
                      {t(`${BASE}.LIST.OPEN`)}
                    </Button>
                  )}

                  {isDone ? (
                    <Pill key="completed" prominence="success" icon={<SuccessIcon />} role="status">
                      {t(`${BASE}.LIST.COMPLETED`)}
                    </Pill>
                  ) : (
                    <Checkbox
                      key="complete"
                      checked={checked}
                      invalid={formError === 'SUBMIT'}
                      onChange={handleCheckboxChange}
                    >
                      {t(`${BASE}.LIST.DONE`)}
                    </Checkbox>
                  )}
                </>
              }
            />
          </List>
        )}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          role: undefined,
          'aria-label': undefined,
          children: (
            <>
              <Button key="cancel" prominence="neutral" onClick={handleClose}>
                {t('ACTIONS.CANCEL')}
              </Button>
              {!isDone && !isLoading && (
                <Button key="submit" prominence="success" onClick={handleSubmitClick}>
                  {t(`${BASE}.COMPLETE`)}
                </Button>
              )}
            </>
          ),
        }}
      />
    </Dialog>
  );
}

export default MaterialAssignmentModal;
